import TagManager from 'react-gtm-module';
import { PromotionPageType } from './utils';

export const PromotionEventName = {
  viewPromotion: 'view_promotion',
  selectPromotion: 'select_promotion',
} as const;

export const InteractionLocation = {
  PTT: 'PTT',
  hero: 'hero',
  CatNavCarousel: 'CatNavCarousel',
  PhotoGrid: 'PhotoGrid',
  CollectionAssortment: 'CollectionAssortment',
  BrandValues: 'BrandValues',
  FeaturedBox: 'FeaturedBox',
  ImageComponent: 'ImageComponent',
  EnhancedPlpBanner: 'EnhancedPlpBanner',
  BestSellerRecommendations: 'BestSellerRecommendations',
  ProductGrid: 'ProductGrid',
  ImageText: 'Image-Text',
} as const;

export const PTTTreatmentType = {
  imageOnly: 'imageOnly',
  v1: 'v1',
  v2: 'v2',
  v3: 'v3',
} as const;

export interface BaseTrackPromotionEventProps {
  eventName: (typeof PromotionEventName)[keyof typeof PromotionEventName];
  page: {
    path: string;
    page_type:
      | (typeof PromotionPageType)[keyof typeof PromotionPageType]
      | 'unassigned';
  };
  interaction: {
    coop_brand: string | null;
    interaction_location: (typeof InteractionLocation)[keyof typeof InteractionLocation];
  };
  ecommerce: {
    creative_name: string;
    creative_slot: string;
    promotion_id: string | null;
    promotion_name: string | null;
    isVideo: boolean;
  };
}

export interface HeroTrackPromotionEventProps
  extends BaseTrackPromotionEventProps {
  interaction: {
    coop_brand: string | null;
    interaction_location: typeof InteractionLocation.hero;
  };
}

export interface PTTTrackPromotionEventProps
  extends BaseTrackPromotionEventProps {
  interaction: {
    coop_brand: string | null;
    interaction_location: typeof InteractionLocation.PTT;
  };
}

export interface CatNavCarouselEventProps extends BaseTrackPromotionEventProps {
  interaction: {
    coop_brand: string | null;
    interaction_location: typeof InteractionLocation.CatNavCarousel;
  };
}

export interface PhotoGridEventProps extends BaseTrackPromotionEventProps {
  interaction: {
    coop_brand: string | null;
    interaction_location: typeof InteractionLocation.PhotoGrid;
  };
}

export interface CollectionAssortmentEventProps
  extends BaseTrackPromotionEventProps {
  interaction: {
    coop_brand: string | null;
    interaction_location: typeof InteractionLocation.CollectionAssortment;
  };
}

export interface BrandValuesEventProps extends BaseTrackPromotionEventProps {
  interaction: {
    coop_brand: string | null;
    interaction_location: typeof InteractionLocation.BrandValues;
  };
}

export interface FeaturedBoxEventProps extends BaseTrackPromotionEventProps {
  interaction: {
    coop_brand: string | null;
    interaction_location: typeof InteractionLocation.FeaturedBox;
  };
}

export interface ImageComponentEventProps extends BaseTrackPromotionEventProps {
  interaction: {
    coop_brand: string | null;
    interaction_location: typeof InteractionLocation.ImageComponent;
  };
}

export interface EnhancedPlpBannerEventProps
  extends BaseTrackPromotionEventProps {
  interaction: {
    coop_brand: string | null;
    interaction_location:
      | typeof InteractionLocation.EnhancedPlpBanner
      | typeof InteractionLocation.ImageText;
  };
}

export interface BestSellerRecommendationsEventProps
  extends BaseTrackPromotionEventProps {
  interaction: {
    coop_brand: string | null;
    interaction_location: typeof InteractionLocation.BestSellerRecommendations;
  };
}

export interface ProductGridEventProps extends BaseTrackPromotionEventProps {
  interaction: {
    coop_brand: string | null;
    interaction_location: typeof InteractionLocation.ProductGrid;
  };
}

export const trackPromotionEvent = ({
  eventName,
  page,
  interaction,
  ecommerce,
}:
  | HeroTrackPromotionEventProps
  | PTTTrackPromotionEventProps
  | CatNavCarouselEventProps
  | PhotoGridEventProps
  | CollectionAssortmentEventProps
  | BrandValuesEventProps
  | FeaturedBoxEventProps
  | ImageComponentEventProps
  | EnhancedPlpBannerEventProps
  | BestSellerRecommendationsEventProps
  | ProductGridEventProps) => {
  TagManager.dataLayer({
    dataLayer: {
      page: undefined,
      interaction: undefined,
      ecommerce: undefined,
    },
  }); // Clear ecommerce object
  TagManager.dataLayer({
    dataLayer: {
      event: 'ecom_interaction',
      event_name: eventName,
      page: {
        ...page,
      },
      interaction: {
        ...interaction,
      },
      ecommerce: {
        ...ecommerce,
      },
    },
  });
};
